import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Switch,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import { Semaphore } from './Semaphore';
import { useLogEffect } from '@/util/useLogEffect';
import LockIcon from '@mui/icons-material/Lock';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export const AppleSsoMigrationHack = () => {
  const [updateConfig, { isLoading: isUpdating }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );

  const {
    remoteAppConfig: { ff_appleSsoMigrationHack },
    refetchAppConfig,
  } = useGetAppConfig();

  const { onSettled: doSettled } = useOnSettled();

  const onSettled = useCallback(async () => {
    doSettled();
    await refetchAppConfig();
  }, [doSettled, refetchAppConfig]);

  useLogEffect('ff_appleSsoMigrationHack', ff_appleSsoMigrationHack);

  const onSwitch = useCallback(async () => {
    await updateConfig(
      'therapistAppConfig',
      {
        id: 'current',
        data: { ff_appleSsoMigrationHack: ff_appleSsoMigrationHack ? 0 : 1 },
      },
      {
        onSettled,
      },
    );
  }, [updateConfig, onSettled, ff_appleSsoMigrationHack]);

  // Lock the switch when enabled to prevent accidental disabling
  const isLocked = ff_appleSsoMigrationHack;

  return (
    <Box marginTop="2rem">
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        marginBottom={1}
      >
        <Typography variant="h6">Apple SSO Migration Hack</Typography>
        <Tooltip
          title={
            isLocked
              ? 'This feature flag is critical and cannot be disabled. Disabling it causes data visibility issues for Apple SSO users who have been migrated to Auth0.'
              : 'Enables migration of Apple SSO users to Auth0. Once enabled, this flag should remain enabled to prevent data visibility issues.'
          }
        >
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          {isLocked && (
            <LockIcon
              sx={{ fontSize: '1rem', color: 'text.secondary' }}
            />
          )}
          <Switch
            checked={ff_appleSsoMigrationHack}
            onChange={onSwitch}
            disabled={isLocked}
          />
        </Box>
        <Box
          marginLeft="1rem"
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          {isUpdating && <CircularProgress size={14} />}
          {!isUpdating && <Semaphore enabled={ff_appleSsoMigrationHack} />}
        </Box>
      </Box>
    </Box>
  );
};

